import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'
import flattenChildren from 'react-keyed-flatten-children'
import { isElement } from 'react-is'

// Register the design system's custom text-size tokens so twMerge treats them as
// font-sizes (conflicting with each other / other sizes), not as arbitrary `text-*`
// that collides with text-align/color. Without this, e.g. `text-label-medium` gets
// dropped when combined with `text-right`.
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        {
          text: ['header-huge', 'header-big', 'header-medium', 'header-small'],
        },
        { text: ['body-medium', 'body-small'] },
        {
          text: ['label-huge', 'label-big', 'label-medium', 'label-small'],
        },
      ],
    },
  },
})

export const cn = (...inputs: ClassValue[]) => twMerge(clsx(inputs))

export function extractTextFromChildren(children: React.ReactNode): string {
  const flatChildren = flattenChildren(children)

  let text = ''

  // Process each flattened child
  for (const child of flatChildren) {
    // If child is a string or number, add it to the text
    if (typeof child === 'string' || typeof child === 'number') {
      text += child
    }
    // If child is a React element with children, recursively extract text
    else if (child && isElement(child) && 'props' in child) {
      const props = child.props as { children?: React.ReactNode }
      if (props.children) {
        text += extractTextFromChildren(props.children)
      }
    }
  }

  return text.trim()
}
